/**
 * **What a personal-record card says** (attention-queue R24) — the movement the item names, what was
 * done in it and what stood before, and the session it happened in.
 *
 * The queue's payload names ONE movement and counts how many of the session's movements hold a
 * record; it carries no result. The result, the one before it and the session's lines come from the
 * session read the card already makes (`GET /workout-execution/{id}/display`, through
 * `sessionDetail`) — so the name and the count stand on the first paint and the figures arrive with
 * the read, in a box that was already their size.
 *
 * Pure: no React, no DOM, no query hook, no i18next import.
 */

import { setHeadline } from '~/entities/lift-history';
import type { Translate } from '~/shared/lib/localeTranslate';

import type { ExerciseLine, SessionDetail, SetMark } from './sessionDetail';
import type { RecordFacts } from './triageShape';

export interface RecordDetail {
  /** "82,5 kg × 5" — the named movement's best set in this session; `null` where the read has none. */
  readonly result: string | null;
  /** The best set of the session before it, in the same words. */
  readonly previous: string | null;
  /** The session's lines that went differently from the plan, those with a record first. */
  readonly lines: readonly ExerciseLine[];
  readonly unchanged: number;
  /** Which set marks the lines draw, so the legend names exactly those. */
  readonly marks: ReadonlySet<SetMark>;
}

/**
 * The line of the movement the item names.
 *
 * By the library id where the payload has one — two movements of one session can share a name, and
 * the id is what the payload means. A movement no longer in the library has no id, and is found by
 * the name the server sent; failing both, the first line that holds a record.
 */
function namedLine(record: RecordFacts, lines: readonly ExerciseLine[]): ExerciseLine | null {
  const byId = record.exerciseId === null ? undefined : lines.find((line) => line.exerciseId === record.exerciseId);
  const byName = record.exerciseName === null ? undefined : lines.find((line) => line.name === record.exerciseName);
  return byId ?? byName ?? lines.find((line) => line.verdict === 'record') ?? null;
}

export function recordDetail(
  record: RecordFacts,
  detail: SessionDetail,
  deps: { readonly t: Translate; readonly locale: string },
): RecordDetail {
  const named = namedLine(record, detail.all);
  const withRecord = detail.deviations.filter((line) => line.verdict === 'record');
  const others = detail.deviations.filter((line) => line.verdict !== 'record');
  const lines = [...withRecord, ...others];
  return {
    result: named?.top == null ? null : setHeadline(named.top, deps),
    previous: named?.priorTop == null ? null : setHeadline(named.priorTop, deps),
    lines,
    unchanged: detail.unchanged,
    marks: new Set(lines.flatMap((line) => line.marks)),
  };
}
